import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Container,
  CssBaseline,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Navbar from "../sidebar/SideNavbar";
import * as XLSX from "xlsx";
import AuthApi4 from "../services/authApi";
import moment from "moment";

export default function RenewalReport() {
  const navigate = useNavigate();

  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    renewalInfo();
  }, []);

  // Reset page whenever filteredData changes (pagination fix)
  useEffect(() => {
    setPage(0);
  }, [filteredData]);

  const renewalInfo = async () => {
    const data = {
      records: [
        {
          operator_id: parseInt(localStorage.getItem("ownerId")),
          phone: localStorage.getItem("phoneNumber"),
          type: "renewInfo",
        },
      ],
    };

    try {
      const response = await AuthApi4.renewReport(data);
      const resData = response.data.re || [];
      setReportData(resData);
      setFilteredData(resData);
    } catch (e) {
      console.log("Renewal Report Api Error", e);
    }
  };

  // Excel Export
  const handleExportToExcel = () => {
    const exportData = filteredData.map((row, index) => ({
      "SL NO": index + 1,
      "CUSTOMER ID": row.AGREEMENT_NO,
      "STB NO": row.stb_number,
      "RENEWAL DATE": moment(row.date).format("DD-MM-YYYY HH:mm:ss"),
      "PACK NAME": row.subs_desc,
      "PACK AMOUNT": row.subs_grnd_tot_prc,
      "RENEWAL STATUS": row.status,
      "RESPONSE": row.response,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Renewal Report");
    XLSX.writeFile(workbook, "RenewalReport.xlsx");
  };

  // Pagination
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Date filter
  const handleSearchClick = async () => {
    const data = {
      records: [
        {
          operator_id: parseInt(localStorage.getItem("ownerId")),
          phone: localStorage.getItem("phoneNumber"),
          from_date: startDate,
          to_date: endDate,
          type: "Filter",
        },
      ],
    };

    try {
      const response = await AuthApi4.renewReport(data);
      const resData = response.data.re || [];
      setReportData(resData);
      setFilteredData(resData);
    } catch (e) {
      console.log("Renewal Report Api Error", e);
    }
  };

  // Universal search
  const handleSearchTermChange = (event) => {
    const searchValue = event.target.value.toLowerCase();
    setSearchTerm(searchValue);

    const filtered = reportData.filter((row) => {
      return (
        (row.AGREEMENT_NO || "").toString().toLowerCase().includes(searchValue) ||
        (row.stb_number || "").toLowerCase().includes(searchValue) ||
        (row.subs_desc || "").toLowerCase().includes(searchValue) ||
        (row.status || "").toLowerCase().includes(searchValue) ||
        (row.response || "").toLowerCase().includes(searchValue) ||
        (row.subs_grnd_tot_prc || "").toString().toLowerCase().includes(searchValue) ||
        (row.date ? moment(row.date).utc().format("DD-MM-YYYY hh:mm:ss a") : "")
          .toLowerCase()
          .includes(searchValue)
      );
    });

    setFilteredData(filtered);
  };

  return (
    <div style={{ display: "flex", background: "#F5F5F5" }}>
      <CssBaseline />
      <Navbar />

      <main style={{ flexGrow: 1, padding: "5px", minHeight: "100vh" }}>
        <Box display="flex" justifyContent="space-between" pt={10}>
          <Typography variant="h5">Renewal Report</Typography>
        </Box>

        {/* Date + Buttons */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "center",
            mt: 2,
            mb: 3,
          }}
        >
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />

          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />

          <Button variant="contained" onClick={handleSearchClick}>
            Get Report
          </Button>

          <Button variant="contained" color="primary" onClick={handleExportToExcel}>
            Export
          </Button>
        </Box>

        {/* Search Box */}
        <Box display="flex" justifyContent="flex-end" mb={2} mr={4}>
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchTermChange}
            sx={{ width: 180 }}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        {/* Table */}
        <Container>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {[
                          "SL NO",
                          "CUSTOMER ID",
                          "STB NUMBER",
                          "RENEWAL DATE",
                          "PACK NAME",
                          "PACK AMOUNT",
                          "RENEWAL STATUS",
                          "RESPONSE",
                        ].map((header) => (
                          <TableCell
                            key={header}
                            sx={{ background: "#ff8c00", color: "white" }}
                          >
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredData.length > 0 ? (
                        filteredData
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((row, index) => (
                            <TableRow key={index}>
                              <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                              <TableCell>{row.AGREEMENT_NO}</TableCell>
                              <TableCell>{row.stb_number}</TableCell>
                              <TableCell>
                                {moment(row.date).utc().format("DD-MM-YYYY hh:mm:ss a")}
                              </TableCell>
                              <TableCell>{row.subs_desc}</TableCell>
                              <TableCell>{row.subs_grnd_tot_prc}</TableCell>
                              <TableCell>{row.status}</TableCell>
                              <TableCell>{row.response}</TableCell>
                            </TableRow>
                          ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            No data available
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={filteredData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </main>
    </div>
  );
}
